/**
 * Camera passthrough. One stream at a time (the iOS constraint, TECH_PLAN §1):
 * 'environment' for framing mode, 'user' for reflection mode. Switching modes
 * tears down the previous stream first — this IS the dramaturgical "turn to face
 * you" moment, not a workaround.
 *
 * ⚠️ `start()` must reach `getUserMedia` BEFORE its first `await` (STATUS §16.1):
 * the Rite calls it from the sigil's click handler and iOS rejects the prompt
 * if the user gesture has been consumed. Everything before the call is
 * synchronous on purpose.
 *
 * Stream ownership (audit 2026-10):
 *  - `start()` is latest-wins. Overlapping calls used to leak a stream: the last
 *    to resolve took the ref and the other was never stopped, so the camera
 *    stayed on with nothing holding it. A call that loses stops its own stream.
 *  - `start(facing)` REUSES a live stream of the same facing instead of opening
 *    a second one. The Rite opens the back camera during its own beat (there is
 *    no <video> yet), and the Field then only has to bind it — no second
 *    getUserMedia, no second permission prompt on browsers that re-ask.
 *  - `start()` resolves `true` only when a stream is genuinely live. It used to
 *    swallow every error and resolve, so the Rite recorded `camera: true` for a
 *    refused camera.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Latest } from '../session/guards.js';

export type CameraFacing = 'environment' | 'user';

export interface CameraController {
  videoRef: React.RefObject<HTMLVideoElement>;
  facing: CameraFacing;
  active: boolean;
  error: string | null;
  /** Resolves true only if a camera stream of this facing is live afterwards. */
  start: (facing: CameraFacing) => Promise<boolean>;
  stop: () => void;
}

function liveStream(s: MediaStream | null): s is MediaStream {
  return !!s && s.getVideoTracks().some((t) => t.readyState === 'live');
}

export function useCamera(): CameraController {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const facingRef = useRef<CameraFacing | null>(null);
  const latest = useRef(new Latest());
  const [facing, setFacing] = useState<CameraFacing>('environment');
  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const release = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    facingRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const stop = useCallback(() => {
    // Invalidate any start() still awaiting getUserMedia: if it resolves after
    // this, it must stop its own stream rather than install it.
    latest.current.invalidate();
    release();
    setActive(false);
  }, [release]);

  const bind = useCallback(async (stream: MediaStream) => {
    const v = videoRef.current;
    if (!v) return;
    if (v.srcObject !== stream) v.srcObject = stream;
    await v.play().catch(() => undefined);
  }, []);

  const start = useCallback(
    async (next: CameraFacing): Promise<boolean> => {
      setError(null);

      // Reuse a live stream that already has the facing we want. Synchronous, so
      // no gesture is at stake; it just binds the stream to the current <video>.
      if (facingRef.current === next && liveStream(streamRef.current)) {
        const ticket = latest.current.begin();
        const stream = streamRef.current;
        setFacing(next);
        await bind(stream);
        if (!latest.current.isCurrent(ticket)) return false;
        setActive(true);
        return true;
      }

      // Tear down first — only one camera stream can be live. This also
      // invalidates earlier tickets, so `begin()` below is the newest.
      stop();
      const ticket = latest.current.begin();

      let stream: MediaStream;
      try {
        // ⚠️ Reached before any await. Do not put one above this line.
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: next },
          audio: false,
        });
      } catch (e) {
        if (latest.current.isCurrent(ticket)) {
          setError(e instanceof Error ? e.message : 'camera unavailable');
          setActive(false);
        }
        return false;
      }

      if (!latest.current.isCurrent(ticket)) {
        // Lost the race (a newer start() or a stop()): this stream has no owner.
        stream.getTracks().forEach((t) => t.stop());
        return false;
      }

      streamRef.current = stream;
      facingRef.current = next;
      setFacing(next);
      await bind(stream);
      if (!latest.current.isCurrent(ticket)) {
        // Superseded while play() was pending; whoever won owns the stream ref.
        return false;
      }
      setActive(true);
      return true;
    },
    [stop, bind],
  );

  useEffect(() => () => stop(), [stop]);

  return { videoRef, facing, active, error, start, stop };
}
